// 强制结束专用备份。正常考试仍使用页面原有的数据和检查点。
interface Point { x: number; y: number; yaw?: number }
interface Metric {
    code?: string; name?: string; id?: string | number; value?: number;
    realTimeValue?: number; minValue?: number; maxValue?: number; underMin?: boolean;
}
interface ChartSeries {
    name: string; id?: string | number; unit: string; min: number; max: number;
    color: string; isEvaluated: boolean; dataPoints: { time: number; value: number }[];
}
export interface AttemptSnapshot {
    pointList: Point[];
    circleDatas: { pos: Point; color: string }[];
    chartData: { dataStore: Record<string, ChartSeries>; timeCounter: number } | null;
}
interface Frame {
    arriveCenter?: boolean | number; triggerRetry?: boolean;
    overLimit?: number; onGroundAlarm?: boolean | number; crash?: boolean | number;
    mapPosition?: Point; mapYaw?: number; stepNameList?: string[]; currentStepIndex?: number;
    evaluateData?: Record<string, Metric | Metric[]>;
}
const metrics = [
    ['heightOffset', 'height', 'm'],
    ['positionOffset', 'position', 'm'],
    ['angleOffset', 'headAngle', '°'],
    ['angleSpeedOffset', 'angle', '°/s'],
    ['speedOffset', 'speed', 'm/s'],
] as const
const copy = <T>(value: T): T => JSON.parse(JSON.stringify(value))

export function createAttemptRecorder(colors: Record<string, string>): {
    record: (frame: Frame, getBase: () => AttemptSnapshot) => void;
    getForcedResult: () => AttemptSnapshot | null;
    isForced: () => boolean;
    reset: () => void;
} {
    let snapshot: AttemptSnapshot | null = null
    let waitingForCenter = false
    let hasLeftCenter = false
    let forced = false
    let deviationRecorded = false

    function append(frame: Frame): void {
        if(!snapshot) return
        const position = frame.mapPosition
        const pos = position && Number.isFinite(position.x) && Number.isFinite(position.y)
            ? { x: position.x, y: position.y, yaw: frame.mapYaw } : null
        if(pos) snapshot.pointList.push(pos)
        const values = frame.evaluateData || {}
        const chart = snapshot.chartData || { dataStore: {}, timeCounter: 0 }
        snapshot.chartData = chart
        chart.timeCounter++
        const step = frame.stepNameList?.[frame.currentStepIndex || 0] || ''
        const errors: string[] = []
        metrics.forEach(([code, key, unit]) => {
            const metric = (values[code] || Object.values(values).find(item =>
                !Array.isArray(item) && item.code === code)) as Metric | undefined
            if(!metric || Array.isArray(metric)) return
            if(metric.realTimeValue !== undefined && Number.isFinite(Number(metric.realTimeValue))){
                const series = chart.dataStore[key] || {
                    name: metric.name || '', id: metric.id, unit,
                    min: Number(metric.minValue || 0), max: Number(metric.maxValue || 0),
                    color: colors[key], isEvaluated: false, dataPoints: [],
                }
                chart.dataStore[key] = series
                series.isEvaluated = true
                series.dataPoints.push({ time: chart.timeCounter, value: Number(metric.realTimeValue) })
            }
            // 与 abnormalPrompt 相同：角速度属于自旋，速度属于八字。
            const applicable = key === 'angle' ? /自旋/.test(step) : key === 'speed' ? /字/.test(step) : true
            if(applicable && (Number(metric.value) > Number(metric.maxValue) ||
                ((key === 'angle' || key === 'speed') && metric.underMin))){
                errors.push(key)
            }
        })
        const time = values.time
        if(Array.isArray(time) && time.some(item => Number(item.value) > Number(item.maxValue))){
            errors.push('timeDeviation')
        }
        // 每次考试保留首次超限位置；只记录数据，不触发提示或修改页面的评判状态。
        if(pos && !deviationRecorded && errors.length){
            errors.forEach(key => snapshot?.circleDatas.push({ pos, color: colors[key] }))
            deviationRecorded = true
        }
    }

    return {
        record(frame, getBase) {
            if(forced) return
            const directFinish = frame.overLimit == 1 || Boolean(frame.onGroundAlarm) || Boolean(frame.crash)
            const arrived = Boolean(frame.arriveCenter)
            // triggerRetry 携带的是刚失败的考次数据，不能把它当成新考次。
            const startsNext = waitingForCenter && hasLeftCenter && arrived && !frame.triggerRetry
            if((!snapshot && arrived) || (startsNext && !directFinish)){
                snapshot = copy(getBase())
                waitingForCenter = false
                hasLeftCenter = false
                deviationRecorded = false
            }
            if(snapshot && (!waitingForCenter || directFinish)) append(frame)
            if(directFinish){
                forced = true
                return
            }
            if(frame.triggerRetry){
                waitingForCenter = true
                hasLeftCenter = !arrived
            }else if(waitingForCenter && !arrived){
                hasLeftCenter = true
            }
        },
        getForcedResult: () => forced && snapshot ? copy(snapshot) : null,
        isForced: () => forced,
        reset() {
            snapshot = null
            waitingForCenter = false
            hasLeftCenter = false
            forced = false
            deviationRecorded = false
        },
    }
}
